import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'

export type RiskLevel = 'Unknown' | 'Low' | 'Medium' | 'High' | 'Very High' | 'Extreme'

// CustomerSummary is a stored health-check brief reduced to the values shown on
// a customer row. Values are unformatted; an absent field means the brief
// doesn't carry it and must be shown as unavailable, never as zero.
export interface CustomerSummary {
  customerId: string
  deviceCount?: number
  printVolume?: number
  printVolumeChange?: number
  risk?: RiskLevel
  estimatedFleetValue?: number
  currencyCode?: string
  generatedAt: string
  /** set on the customer's Intel tab */
  lifecycleStage?: string
}

export type CustomerSummariesStatus = 'loading' | 'loaded' | 'error'

export interface CustomerSummariesState {
  summaries: Map<string, CustomerSummary>
  status: CustomerSummariesStatus
}

const fetchSummaries = (teamID: string, signal?: AbortSignal) =>
  axios
    .get<CustomerSummary[]>(`/api/teams/${teamID}/intelligence/customers`, { signal })
    .then(resp => new Map((resp.data ?? []).map(summary => [summary.customerId, summary])))

const NO_SUMMARIES = new Map<string, CustomerSummary>()

export function useCustomerSummaries(teamID: string): CustomerSummariesState & { reload: () => void } {
  const [state, setState] = useState<CustomerSummariesState>({ summaries: NO_SUMMARIES, status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ summaries: NO_SUMMARIES, status: 'loading' })

    fetchSummaries(teamID, controller.signal)
      .then(summaries => setState({ summaries, status: 'loaded' }))
      .catch(err => {
        if (axios.isCancel(err)) return
        setState({ summaries: NO_SUMMARIES, status: 'error' })
      })

    return () => {
      controller.abort()
    }
  }, [teamID])

  // Keeps the current rows on screen while refetching; a failed reload leaves them as they were.
  const reload = useCallback(() => {
    fetchSummaries(teamID)
      .then(summaries => setState({ summaries, status: 'loaded' }))
      .catch(() => undefined)
  }, [teamID])

  return { ...state, reload }
}
